import React, { useState, Component, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  BookOpen, 
  Users, 
  BarChart3,
  Menu,
  X,
  Shield,
  Crown,
  GraduationCap,
  Briefcase,
  FolderOpen,
  Users2,
  Database,
  AlertTriangle,
  RefreshCw,
  XCircle,
  Video,
  Trash2,
  ChevronDown,
  LogOut,
  User,
  Settings,
  Bell,
  Search,
  MessageCircle,
  MessageSquare,
  Activity,
  Target,
  UserPlus,
  Lock,
  Brain,
  HelpCircle,
  Building2,
  CreditCard,
  Banknote,
  Sparkles,
  BookOpen as BookOpenIcon,
  Heart
} from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';
import { getAdminPermissions } from '../../firebase/collections';

// Error Boundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen admin-content flex items-center justify-center p-4">
          <div className="admin-card p-6 sm:p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="admin-section-title mb-2">Admin Panel Error</h2>
              <p className="admin-section-subtitle mb-4">
                There was an error loading the admin panel. Please try refreshing the page.
              </p>
              <button 
                onClick={() => window.location.reload()} 
                className="admin-btn-primary flex items-center mx-auto"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [allowedSections, setAllowedSections] = useState(null);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useRealtime();

  // Enhanced device detection
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Close sidebar when route changes on mobile and tablet
  useEffect(() => {
    if (isMobile || isTablet) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile, isTablet]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Load admin permissions
  useEffect(() => {
    const loadPermissions = async () => {
      if (!currentUser?.email) {
        setLoadingPermissions(false);
        return;
      }

      // Check if user is founder or superadmin - they have full access
      const isFounder = userProfile?.role === 'founder' || currentUser.email.includes('founder');
      const isSuperAdmin = userProfile?.role === 'superadmin';
      
      if (isFounder || isSuperAdmin) {
        setAllowedSections(null); // null means all sections allowed
        setLoadingPermissions(false);
        return;
      }

      try {
        const result = await getAdminPermissions(currentUser.email);
        if (result.success && result.data) {
          setAllowedSections(result.data.permissions || []);
        } else {
          // No permissions set - show all sections (backward compatibility)
          setAllowedSections(null);
        }
      } catch (error) {
        console.error('Error loading permissions:', error);
        setAllowedSections(null); // Default to all sections on error
      } finally {
        setLoadingPermissions(false);
      }
    };

    loadPermissions();
  }, [currentUser?.email, userProfile?.role]);

  // Helper function to check if section is allowed
  const isSectionAllowed = (href) => {
    // If no permissions set (null), allow all (founder/superadmin or no restrictions)
    if (allowedSections === null) return true;
    
    // Extract section ID from href (e.g., '/admin/user-management' -> 'user-management')
    let sectionId = href.replace('/admin/', '').replace('/admin', '');
    
    // Handle dashboard route
    if (!sectionId || sectionId === '') {
      sectionId = 'dashboard';
    }
    
    // Check if section is in allowed list
    return allowedSections.includes(sectionId);
  };

  // Dynamic navigation based on permissions
  // Complete navigation structure with all admin routes
  const allNavigation = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: Home,
      section: 'main'
    },
    {
      name: 'Exam Management',
      href: '/admin/exam-management',
      icon: Shield,
      section: 'management'
    },
    {
      name: 'Test Management',
      href: '/admin/test-management',
      icon: FileText,
      section: 'management'
    },
    {
      name: 'Live Class Management',
      href: '/admin/live-class-management',
      icon: Video,
      section: 'management'
    },
    {
      name: 'Course Management',
      href: '/admin/course-management',
      icon: GraduationCap,
      section: 'management'
    },
    {
      name: 'Resource Management',
      href: '/admin/resource-management',
      icon: FolderOpen,
      section: 'management'
    },
    {
      name: 'User Management',
      href: '/admin/user-management',
      icon: Users,
      section: 'management'
    },
    {
      name: 'Tuition Teachers',
      href: '/admin/tuition-teachers',
      icon: Users2,
      section: 'management'
    },
    {
      name: 'Coaching Management',
      href: '/admin/coaching-management',
      icon: Building2,
      section: 'management'
    },
    {
      name: 'Coaching Admin Permissions',
      href: '/admin/coaching-admin-permissions',
      icon: Shield,
      section: 'management'
    },
    {
      name: 'All Applications',
      href: '/admin/all-applications',
      icon: FileText,
      section: 'management'
    },
    {
      name: 'Refunds',
      href: '/admin/refunds',
      icon: CreditCard,
      section: 'management'
    },
    {
      name: 'Settlements',
      href: '/admin/settlements',
      icon: Banknote,
      section: 'management'
    },
    {
      name: 'All Institutions',
      href: '/admin/all-institutions',
      icon: Building2,
      section: 'management'
    },
    {
      name: 'Institution Management',
      href: '/admin/institution-management',
      icon: Building2,
      section: 'management'
    },
    {
      name: 'Create Institute Account',
      href: '/admin/create-institute-account',
      icon: Building2,
      section: 'management',
      badge: 'Offline'
    },
    {
      name: 'Teacher Requests',
      href: '/admin/teacher-requests',
      icon: MessageSquare,
      section: 'management'
    },
    {
      name: 'Vacancy Management',
      href: '/admin/vacancy-management',
      icon: UserPlus,
      section: 'management'
    },
    {
      name: 'Batch Management',
      href: '/admin/batch-management',
      icon: Users,
      section: 'management'
    },
    {
      name: 'Help Requests',
      href: '/admin/help-requests',
      icon: MessageCircle,
      section: 'management'
    },
    {
      name: 'Support Tickets',
      href: '/admin/support-tickets',
      icon: HelpCircle,
      section: 'management'
    },
    {
      name: 'Educational Support',
      href: '/admin/educational-support',
      icon: Heart,
      section: 'management'
    },
    {
      name: 'Doubts Management',
      href: '/admin/doubts-management',
      icon: AlertTriangle,
      section: 'management'
    },
    {
      name: 'AI Knowledge Management',
      href: '/admin/ai-knowledge',
      icon: Brain,
      section: 'management'
    },
    {
      name: 'Team Management',
      href: '/admin/team-management',
      icon: Users,
      section: 'management'
    },
    {
      name: 'System Settings',
      href: '/admin/system-settings',
      icon: Settings,
      section: 'management'
    },
    {
      name: 'Stats Manager',
      href: '/admin/stats-manager',
      icon: BarChart3,
      section: 'management'
    },
    {
      name: 'Poll Management',
      href: '/admin/poll-management',
      icon: Activity,
      section: 'management'
    },
    {
      name: 'Activity Registration',
      href: '/admin/activity-registration',
      icon: Target,
      section: 'management'
    },
    {
      name: 'Leaderboard Management',
      href: '/admin/leaderboard-management',
      icon: Crown,
      section: 'management'
    },
    {
      name: 'Proctoring',
      href: '/admin/proctoring',
      icon: Shield,
      section: 'management'
    },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: BarChart3,
      section: 'analytics'
    },
    {
      name: 'Data Migration',
      href: '/admin/data-migration',
      icon: Database,
      section: 'tools'
    },
    {
      name: 'Performance Monitor',
      href: '/admin/performance-monitor',
      icon: BarChart3,
      section: 'tools'
    },
    {
      name: 'Firebase Index Helper',
      href: '/admin/firebase-index-helper',
      icon: Database,
      section: 'tools'
    },
    {
      name: 'Blog Management',
      href: '/admin/blog-management',
      icon: BookOpenIcon,
      section: 'management'
    },
    {
      name: 'Unschooling Mode',
      href: '/admin/unschooling-management',
      icon: Sparkles,
      section: 'management'
    }
  ];

  // Filter navigation based on permissions
  const navigation = loadingPermissions 
    ? [] 
    : allNavigation.filter(item => isSectionAllowed(item.href));

  const groupedNavigation = navigation.reduce((acc, item) => {
    if (!acc[item.section]) {
      acc[item.section] = [];
    }
    acc[item.section].push(item);
    return acc;
  }, {});

  const sectionNames = {
    main: 'Main',
    management: 'Management',
    analytics: 'Analytics',
    tools: 'Tools'
  };


  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      // Error signing out
      // Fallback to redirect even if signOut fails
      await signOut(auth).catch(() => {});
      navigate('/');
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen admin-content flex">
        {/* Mobile and tablet sidebar overlay */}
        {sidebarOpen && (isMobile || isTablet) && (
          <div 
            className="fixed inset-0 z-40 bg-black bg-opacity-50 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={`fixed inset-y-0 left-0 z-50 w-64 sm:w-72 admin-sidebar shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
            sidebarOpen ? 'translate-x-0' : ((isMobile || isTablet) ? '-translate-x-full' : 'lg:translate-x-0')
          }`}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 admin-sidebar-header">
            <Link to="/admin" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <Shield size={16} className="text-white" />
              </div>
              <span className="text-lg sm:text-xl font-bold text-white">Admin</span>
            </Link>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSidebarOpen(false);
              }}
              className="lg:hidden p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors touch-manipulation"
              aria-label="Close sidebar"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="mt-6 px-3 flex-1 overflow-y-auto">
            {loadingPermissions ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            ) : navigation.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400">No sections available</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedNavigation).map(([section, items]) => (
                <div key={section}>
                  {/* Section Header */}
                  <div className="flex items-center justify-between px-3 py-2">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {sectionNames[section]}
                    </h3>
                  </div>

                  {/* Section Items */}
                  <div className="space-y-1">
                    {items.map((item) => {
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`admin-sidebar-nav ${isActive ? 'active' : ''}`}
                          onClick={() => (isMobile || isTablet) && setSidebarOpen(false)}
                        >
                          <item.icon size={20} className="mr-3 flex-shrink-0" />
                          <span className="truncate">{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            )}
          </nav>

          {/* Admin Badge */}
          <div className="absolute bottom-16 left-0 right-0 px-4">
            <div className="admin-glass-card text-white px-3 py-2 rounded-lg text-center text-sm font-medium">
              <Shield size={16} className="inline mr-2" />
              Admin Access
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top Navigation Bar */}
          <header className="admin-header admin-header-gradient">
            <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
              {/* Left side - Menu button and search */}
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden admin-btn-ghost"
                >
                  <Menu size={20} />
                </button>

                {/* Search - Hidden on mobile, shown on tablet+ */}
                <div className="hidden sm:block admin-search">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search admin..."
                    className="admin-search-input w-64 lg:w-80"
                  />
                </div>
              </div>

              {/* Right side - Actions and user menu */}
              <div className="flex items-center space-x-3">
                {/* Mobile search button */}
                <button
                  onClick={() => setShowSearch(!showSearch)}
                  className="sm:hidden admin-btn-ghost"
                >
                  <Search size={20} />
                </button>

                {/* Notifications */}
                <button className="admin-btn-ghost relative">
                  <Bell size={20} />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                </button>

                {/* Settings */}
                <button className="admin-btn-ghost">
                  <Settings size={20} />
                </button>

                {/* User menu */}
                <div className="relative user-menu">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="admin-btn-ghost flex items-center space-x-2"
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-red-600 to-orange-600 rounded-full flex items-center justify-center shadow-md">
                      <User size={16} className="text-white" />
                    </div>
                    <ChevronDown size={16} className={`transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {/* User dropdown menu */}
                  {showUserMenu && (
                    <div className="admin-dropdown">
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {userProfile?.displayName || currentUser?.displayName || 'Admin User'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {currentUser?.email || 'admin@mahstry.com'}
                        </p>
                        {allowedSections !== null && allowedSections.length > 0 && (
                          <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">
                            {allowedSections.length} section{allowedSections.length !== 1 ? 's' : ''} allowed
                          </p>
                        )}
                      </div>
                      <button
                        onClick={handleLogout}
                        className="admin-dropdown-item"
                      >
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile search bar */}
            {showSearch && (
              <div className="sm:hidden px-4 pb-4">
                <div className="admin-search">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search admin..."
                    className="admin-search-input"
                    autoFocus
                  />
                </div>
              </div>
            )}
          </header>

          {/* Main content area */}
          <main className="flex-1 overflow-auto admin-responsive-padding">
            <div className="container-mobile">
              {/* Rendering children in AdminLayout */}
              {children}
            </div>
          </main>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default AdminLayout;
